<script setup lang="ts">
import { useColorMode } from "@vueuse/core";
import {
    myStores, isMyStoresLoading,
    // storesWithNegativeBalance, loadMyStores
} from './MyStoresStore';
// import StoreBalance from "@orders-spa-vue/pages/StoreBalance.vue";
import { playBlankAudioLoop, isAbleToPlayAudio, watchNewOrders } from "./playAudio";
import { storeNotifierChannels } from "./listenStoreNotifier";
import { MySettingsStore } from "./MySettingsStore";
import DialogProvider from "@/ui-plus/dialog/DialogProvider.vue";
import { Toaster } from "@/ui-plus/sonner";
import {
    Printer, Settings2, Loader2, Play, AlertCircle, Store,
    // RefreshCw
} from "lucide-vue-next";
import PairWidget from "./PairWidget.vue";
import { onMounted, ref, computed, watch } from "vue";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import PrintOptionsDropdown from "./PrintOptionsDropdown.vue";
import {
    Dialog,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogScrollContent,
    DialogClose,
    DialogFooter,
    DialogTrigger,
} from "@/components/ui/dialog";
import SettingsView from "./SettingsView.vue";
// import OrderNotificationEmailPrompt from "./OrderNotificationEmailPrompt.vue";
import StoreHolidayNoticeForm from "@orders-spa-vue/pages/StoreHolidayNoticeForm.vue";
import {
    holidayNowTick,
    initHolidayNoticeStatus,
    isStoreHolidayActiveNow,
    loadHolidayNotice,
} from "./holidayNoticeStatus.store";
import {
    getStoreHostname,
    loadStoreHostname,
} from "./storeHostname.store";
import {
    Select,
    SelectTrigger,
    SelectValue,
    SelectContent,
    SelectItem
} from '@/components/ui/select';
import { currentPrinter, currentPrinters } from "./getPrinters";
import {
    NumberField,
    NumberFieldContent,
    NumberFieldDecrement,
    NumberFieldIncrement,
    NumberFieldInput,
} from '@/components/ui/number-field'
import { Label } from '@/components/ui/label';
import { copies, printAfterAccept, printOrderOnPlaced } from "./copiesNumber";
import Dropdown from './Dropdown.vue';
import { Switch } from '@/components/ui/switch';
import OrderCard from "./OrderCard.vue";
import { cachedStoreOrders } from "./RecentOrdersStore";
import {
    autoStartOnLogin,
    autoStartLoading,
    autoStartPlatformLabel,
    isAutoStartSupported,
    refreshAutoStartStatus,
    toggleAutoStart,
} from "./autoStart";

const mode = useColorMode();
const toasterTheme = computed(() => {
    if (mode.value === "auto") {
        return "system";
    } else {
        return mode.value;
    }
});

const orders = computed(() => {
    return Object.values(cachedStoreOrders.value).sort((a, b) => b.order_placed_at_ts - a.order_placed_at_ts);
});


onMounted(() => {
    playBlankAudioLoop();
    watchNewOrders();
    initHolidayNoticeStatus();
    if (isAutoStartSupported) {
        refreshAutoStartStatus();
    }
});

const isSettingsDialogOpen = ref(false);

const enabledStores = computed(() => {
    return myStores.value.filter((store) => MySettingsStore.value[store.id]?.isEnabled);
});

watch(enabledStores, (stores) => {
    for (const store of stores) {
        void loadStoreHostname(store.id);
    }
}, { immediate: true });

const isHolidayClosed = (storeId: string) => {
    void holidayNowTick.value;
    return isStoreHolidayActiveNow(storeId);
};

const handlePrinterChange = (deviceName: string) => {
    const selectedPrinter = currentPrinters.value.find(printer => printer.deviceName === deviceName);
    if (selectedPrinter) {
        currentPrinter.value = selectedPrinter;
    }
};

</script>

<template>
    <div>
        <div class="p-4 space-y-8 max-w-3xl mx-auto">

            <h1 class="text-3xl font-bold py-2">
                <Dropdown />
                Auto Print
                <Printer class="inline-block ml-2" />

                <div v-if="isAutoStartSupported"
                     class="space-y-2 inline-block ml-4">
                    <Label>Start on login ({{ autoStartPlatformLabel }})</Label>
                    <Switch :checked="autoStartOnLogin"
                            :disabled="autoStartLoading"
                            @update:checked="toggleAutoStart" />
                    <p class="text-xs text-muted-foreground">
                        Starts Autoprint when you sign in to this computer.
                    </p>
                </div>

            </h1>

            <div v-if="currentPrinters.length === 0"
                 class="mb-4 bg-destructive text-destructive-foreground rounded-md p-4 font-bold">
                <AlertCircle class="mr-2 inline-block" />
                No printers found!
                <br>
                Add a printer to your device then restart the app.
            </div>


            <div v-if="isMyStoresLoading">
                <div class="flex justify-center items-center h-screen">
                    <Loader2 class="w-20 h-20 animate-spin" />
                </div>
            </div>

            <!-- <details class="mb-4 rounded-md border border-border p-3 text-xs">
                <summary class="cursor-pointer select-none font-bold">
                    Letter from developer Zemega.com
                </summary>
                <div class="mt-3 space-y-2 font-normal leading-relaxed text-muted-foreground">
                    <p>
                        Hi — I'm Kerem, the independent developer behind zemega.com.
                    </p>
                    <p>
                        Onlinebite is my support partner. They handle your support, menu updates, and day-to-day help. I
                        maintain the software.
                    </p>
                    <p>
                        For the last 3–4 weeks I couldn't cover the platform from fees. I wasn't earning enough, and SMS
                        and hosting came out of my own pocket.
                    </p>
                    <p>
                        Zemega.com is still free for restaurants/stores. There’s no subscription, and we never take a
                        cut of
                        your menu prices.
                        Instead, buyers pay a small extra service fee on top of their order. It is not taken from your
                        menu price. This
                        fee covers SMS, hosting, and development. The same applies to the fees for our support and
                        marketing partners.
                    </p>
                    <p>
                        You can check your store balance in this app anytime.
                    </p>
                    <p class="text-base font-bold text-foreground">
                        Ideas or feedback: kerem@zemega.com
                    </p>
                </div>
            </details> -->

            <!-- <details v-if="storesWithNegativeBalance.length > 0 && !isMyStoresLoading"
                     class="mb-4 bg-destructive text-destructive-foreground rounded-md p-4 font-bold text-xs"
                     :open="storesWithNegativeBalance.length <= 2">
                <summary class="cursor-pointer select-none">
                    Negative balance
                    <template v-if="storesWithNegativeBalance.length > 1">
                        ({{ storesWithNegativeBalance.length }} stores)
                    </template>
</summary>
<p class="mt-3 font-normal">
    All payment methods (except Stripe) are disabled until you have a positive balance.
</p>
<div class="mt-3 flex flex-wrap items-center gap-2">
    <Dialog v-for="store in storesWithNegativeBalance" :key="store.id">
        <DialogTrigger>
            <Button variant="outline" size="sm">
                '{{ store.name?.substring(0, 17) }}'
            </Button>
        </DialogTrigger>
        <DialogScrollContent class="w-full max-w-2xl">
            <DialogHeader>
                <DialogTitle>Balance and Transactions</DialogTitle>
                <DialogDescription>
                    Current balance and transactions
                </DialogDescription>
            </DialogHeader>
            <StoreBalance :currentStoreId="store.id" />
        </DialogScrollContent>
    </Dialog>
    <Button variant="outline" size="sm" @click="loadMyStores()">
        <RefreshCw class="h-4 w-4" />
    </Button>
</div>
</details> -->


            <div class="mb-4 bg-destructive text-destructive-foreground rounded-md p-2 text-sm"
                 v-if="!isMyStoresLoading && !isAbleToPlayAudio">
                To enable audio notifications, please click the button
                <br>
                or enable the audio permission in your browser settings.
                <br>
                <Button class="mt-2"
                        variant="outline"
                        @click="playBlankAudioLoop()">
                    <Play class="inline-block mr-1" />
                    Enable audio notifications
                </Button>
            </div>

            <div class="mb-4 bg-destructive text-destructive-foreground rounded-md p-4 font-bold"
                 v-if="myStores.length === 0 && !isMyStoresLoading">
                Not paired with any store
                <br>
                please pair with a store to start using
                <br>
                <PairWidget />
            </div>


            <div class="mb-4 bg-destructive text-destructive-foreground rounded-md p-4 font-bold"
                 v-if="myStores.length > 0 && Object.keys(storeNotifierChannels).length === 0 && !isMyStoresLoading">
                There is no store enabled.
                <br>
                Please open the settings and enable a store to receive orders in real time.
                <br>
                <Button @click="isSettingsDialogOpen = true;"
                        variant="outline">
                    <Settings2 class="inline-block mr-1" />
                    Settings
                </Button>
            </div>
            <!-- <div class="mb-4 bg-destructive text-destructive-foreground rounded-md p-4 font-bold"
                 v-if="myStores.length > 0 && Object.keys(storeNotifierChannels).length === 0 && !isMyStoresLoading">
                Connection error, will try to reconnect.
            </div> -->

            <!-- <div class="mb-4 border border-bg-foreground rounded-md p-4 font-bold">
                <AlertCircle class="mr-2 inline-block" />
                <span class="text-lg">Important:</span>
                Do not quit the app in your working hours to receive new orders instantly.
                <br>
                (you may close this window, closing this window will not quit the app)
                <br>
                <span class="text-sm text-muted-foreground">
                    If you do not accept orders after 25 minutes, they will be automatically cancelled and
                    refunded.
                </span>
            </div> -->

            <!-- <br>
        To prevent automatic cancellations, you can enable auto-acceptance in Settings → Store →
        Automations. -->

            <div class="flex gap-2 justify-between items-center flex-wrap">
                <div class="flex gap-2 flex-wrap items-center mb-4">
                    <Button variant="outline"
                            @click="isSettingsDialogOpen = true;">
                        <Settings2 class="inline-block mr-1" />
                        Settings
                    </Button>

                    <!-- <OrderNotificationEmailPrompt v-for="store in enabledStores"
                                                  :key="'email-' + store.id"
                                                  :store-id="store.id"
                                                  :store-name="enabledStores.length > 1 ? store.name : undefined" /> -->

                    <Dialog v-for="store in enabledStores"
                            :key="store.id">
                        <DialogTrigger as-child>
                            <Button variant="outline">
                                <Store class="inline-block mr-1" />

                                <span v-if="getStoreHostname(store.id)"
                                      class="max-w-[12rem] truncate">{{ getStoreHostname(store.id) }} -></span>
                                <span v-else-if="enabledStores.length > 1"
                                      class="max-w-[8rem] truncate">{{ store.name }}</span>

                                <Badge v-if="isHolidayClosed(store.id)"
                                       variant="destructive"
                                       class="ml-1">
                                    Closed/Holiday
                                </Badge>
                                <span v-else
                                      class="font-bold text-primary">Ready for new orders</span>
                            </Button>
                        </DialogTrigger>
                        <DialogScrollContent>
                            <DialogHeader>
                                <DialogTitle>
                                    Open/Closed — {{ store.name }}
                                </DialogTitle>
                                <DialogDescription>
                                    Show a warning to buyers and block checkout while you are closed.
                                </DialogDescription>
                            </DialogHeader>
                            <StoreHolidayNoticeForm :store-id="store.id"
                                                    @saved="loadHolidayNotice(store.id)" />
                        </DialogScrollContent>
                    </Dialog>
                </div>
                <!-- <div
                     class="text-xs text-muted-foreground no-scrollbar overflow-x-auto overflow-y-hidden whitespace-nowrap">
                    Enabled Stores: {{enabledStores.map(store => store.name).join(', ')}}
                </div> -->
            </div>

            <Dialog v-model:open="isSettingsDialogOpen">
                <DialogScrollContent class="w-full max-w-7xl">
                    <DialogHeader>
                        <DialogTitle>Settings</DialogTitle>
                        <DialogDescription />
                    </DialogHeader>

                    <div v-if="currentPrinters.length > 0"
                         class="mb-4">
                        <label class="block text-sm font-medium mb-2">Default Printer:</label>
                        <Select :model-value="currentPrinter?.deviceName"
                                @update:model-value="handlePrinterChange">
                            <SelectTrigger class="w-full">
                                <SelectValue placeholder="Select a printer" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem v-for="printer in currentPrinters"
                                            :key="printer.deviceName"
                                            :value="printer.deviceName">
                                    {{ printer.printerName || printer.deviceName }}
                                </SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    <div v-else
                         class="mb-4 bg-destructive text-destructive-foreground rounded-md p-4 font-bold">
                        <AlertCircle class="mr-2 inline-block" />
                        No printers found!
                        <br>
                        Add a printer to your device then restart the app.
                    </div>

                    <div class="space-y-2">
                        <Label>Copies</Label>
                        <NumberField id="copies"
                                     class="max-w-40 pt-8"
                                     v-model="copies"
                                     :default-value="1"
                                     :step="1"
                                     :min="1">
                            <NumberFieldContent>
                                <NumberFieldDecrement
                                                      class="bg-secondary rounded-l-md hover:bg-secondary/60 cursor-pointer" />
                                <NumberFieldInput />
                                <NumberFieldIncrement
                                                      class="bg-secondary rounded-r-md hover:bg-secondary/60 cursor-pointer" />
                            </NumberFieldContent>
                        </NumberField>
                    </div>

                    <div class="space-y-2">
                        <Label>Auto-print after accept order</Label>
                        <Switch v-model:checked="printAfterAccept" />
                    </div>

                    <div class="space-y-2">
                        <Label>Auto-print new order detected</Label>
                        <Switch v-model:checked="printOrderOnPlaced" />
                    </div>

                    <div>
                        <PrintOptionsDropdown>
                            <template #trigger>
                                <Button variant="outline"
                                        class="mb-4">
                                    <Printer class="mr-1" />
                                    Size and boldness
                                </Button>
                            </template>
                        </PrintOptionsDropdown>
                    </div>


                    <PairWidget />

                    <SettingsView />

                    <DialogFooter>
                        <DialogClose as-child>
                            <Button variant="secondary"
                                    class="w-full">
                                Close
                            </Button>
                        </DialogClose>
                    </DialogFooter>
                </DialogScrollContent>
            </Dialog>

            <div class="space-y-4">
                <div v-for="order in orders"
                     :key="order.storeOrderId"
                     class="block">
                    <OrderCard :order="order" />
                </div>
            </div>


        </div>
        <DialogProvider />
        <Toaster :theme="toasterTheme"
                 closeButton
                 richColors
                 class="pointer-events-auto" />
    </div>
</template>